/** Phase 1 reason/owner/fix contract. These rows explain holds; they never grant a write. */
import { periodReapprovalReason } from './period-reapproval';

/** Shared ownership for raw insert gates and their readiness vocabulary aliases. */
export function timesheetApprovalHandoff(code: string | null | undefined) {
  const key = (code ?? '').toUpperCase();
  if (
    /^(?:PAY_RUN_ITEM_TIMESHEET_(?:PAY_CERTIFICATION_STALE|LINEAGE_REQUIRED|REVISION_MISMATCH|APPROVAL_MISMATCH|LEGACY_RECEIPT_NEEDS_REAPPROVAL|REAPPROVAL_REQUIRED)|PAYROLL_TIMESHEET_HOLD_APPROVAL_SUPERSEDED|ALLOWANCE_PAY_CERTIFICATION_REQUIRED|LEGACY_RECEIPT_NEEDS_REAPPROVAL|TIMESHEET_(?:REAPPROVAL_REQUIRED|REVISION_APPROVAL_SUBMISSION_STALE|REVISION_APPROVAL_STALE_OR_INVALID_STATE|APPROVAL_STALE_WORKED_TIME|APPROVAL_STALE_PAY_REVIEW|FACT_SET_CHANGED_PAY_REAPPROVAL_REQUIRED)|REAPPROVAL_REQUIRED)$/.test(
      key,
    )
  )
    return {
      owner: 'Payroll officer: re-review this timesheet in Timesheets',
      reason:
        'This timesheet’s approval is out of date. In Timesheets, review its current time and pay details, then Reopen the approval and approve again.',
      kind: 'REAPPROVAL' as const,
    };
  if (
    /^(?:PAY_RUN_ITEM_TIMESHEET_NOT_APPROVED|TIMESHEET_NOT_APPROVED|NOT_APPROVED|PENDING_MANAGER_APPROVAL|TIMESHEET_PENDING_MANAGER_APPROVAL|PAYROLL_TIMESHEET_HOLD_(?:PENDING_MANAGER_APPROVAL|NOT_APPROVED))$/.test(
      key,
    )
  )
    return {
      owner: 'Timesheet approver: approve in Timesheets',
      reason:
        'This timesheet is awaiting approval. A timesheet approver must review and approve it in Timesheets.',
      kind: 'TIMESHEET' as const,
    };
  return null;
}

export function payRoleShiftCount(
  jobs: readonly { sourceKind: string; sourceId: string; reasonCode: string | null }[],
  total: number,
): number | null {
  if (total !== jobs.length) return null;
  return new Set(
    jobs
      .filter((job) => payrollBlockerKind(job.reasonCode) === 'PAY_ROLE')
      .map((job) => `${job.sourceKind}:${job.sourceId}`),
  ).size;
}

const SALARY_HOLD_REASONS: Record<string, string> = {
  payroll_salary_hold_assignment_gap:
    'Salary assignment dates leave part of this pay period uncovered. Correct the assignment dates or record an approved ad-hoc payment.',
  payroll_salary_hold_assignment_overlap:
    'Salary assignments overlap in this pay period. Correct their effective dates before rebuilding.',
  payroll_salary_hold_terms_change_in_period:
    'Salary terms change during this pay period. Correct the effective dates or record an approved ad-hoc payment; salary is not prorated.',
  payroll_salary_hold_profile_gap_or_overlap:
    'Active salary profiles leave a gap, overlap, or point to the wrong assignment. Correct the profile dates and links.',
  payroll_salary_hold_non_salary_profile:
    'An active award profile overlaps this salary period. End the award profile and record the effective salary profile.',
  payroll_salary_hold_method_mismatch:
    'The worker payment method disagrees with the salary assignment. Correct the worker pay setup.',
};
const TIMESHEET_HOLD_REASONS: Record<string, string> = {
  payroll_timesheet_hold_approval_superseded:
    'This timesheet’s approval is out of date. In Roster > Timesheets, use Reopen approval, check the values and any overlapping timesheet, then Approve. In Payroll, choose Build a new version. Bulk Re-approve applies only when the approved values have not changed and there is no overlap.',
};
const ENGINE_HOLD_REASONS: Record<string, string> = {
  pricing_engine_revision_invalid:
    'Statura support: the pay calculation setup needs review. This pay input remains unpaid until support corrects the version and payroll is rebuilt.',
  pricing_cost_centre_capture_missing:
    'No cost centre was saved with this pay input. In Payroll, choose Build a new version so the worker’s cost centre is captured again. It stays unpaid until then.',
};

export function supersededTimesheetRemedy(exclusionReason: unknown): {
  reason: string;
  nextStep: string;
} {
  if (exclusionReason === 'CAPTURED_VALUES_DIFFER')
    return {
      reason: 'Values changed after approval',
      nextStep:
        'Roster > Timesheets → Reopen approval → check → Approve, then Payroll → Build a new version. Acknowledge the current hold only as unpaid.',
    };
  if (exclusionReason === 'APPROVAL_OVERLAP')
    return {
      reason: 'Overlaps another timesheet',
      nextStep:
        'Roster > Timesheets → Reopen approval → fix the times or reject the duplicate → Approve, then Payroll → Build a new version. Reopen an approved duplicate before rejecting it. Acknowledge the current hold only as unpaid.',
    };
  return {
    reason: 'This timesheet’s approval is out of date',
    nextStep:
      'Roster > Timesheets → Reopen approval → check changed values and resolve any overlap → Approve, then Payroll → Build a new version. Bulk Re-approve applies only to unchanged values without an overlap. Acknowledge the current hold only as unpaid.',
  };
}

export type PayrollBlockerKind =
  | 'PAY_ROLE'
  | 'STOPPED'
  | 'TIMESHEET'
  | 'REAPPROVAL'
  | 'SELF_REVIEW'
  | 'OFFICER_SETUP'
  | 'REAPPROVAL_OFF'
  | 'HELD_SOURCE'
  | 'FROZEN_SOURCE'
  | 'XERO_DRAFT'
  | 'XERO_MAPPING'
  | 'XERO_SUPER'
  | 'XERO_DEDUCTIONS'
  | 'XERO_LOADING'
  | 'XERO_BUDGET'
  | 'XERO_CONNECTION'
  | 'UPDATED_PLAN'
  | 'PARTIAL_SEND'
  | 'PAYMENT_EVIDENCE'
  | 'SYSTEM';
export type PayrollBlockerContext = {
  id: string;
  person: string;
  date: string;
  workerId?: string | null;
  sourceId?: string;
  sourceType?: string;
  sourceRequestId?: string;
  reasonCode?: string | null;
  reason?: string;
  returnHref: string;
  payRunId?: string | null;
  approved?: boolean;
  capacityReturnsAt?: string;
};
export type PayrollBlockerRow = {
  id: string;
  person: string;
  date: string;
  reason: string;
  owner: string;
  fix: { label: string; href: string };
  caption?: string;
};
export const PAYROLL_BLOCKER_MATRIX: Record<
  PayrollBlockerKind,
  { reason: string; owner: string; label: string; path: string }
> = {
  PAY_ROLE: {
    reason: 'No pay role applies to this shift.',
    owner: 'Payroll officer, finance officer or administrator',
    label: 'Set pay role',
    path: '/settings/payroll#pay-identity',
  },
  STOPPED: {
    reason:
      'Automatic retries have stopped. This does not exclude or pay the source; it still needs valid pricing evidence to be paid.',
    owner: 'Payroll officer, finance officer or administrator',
    label: 'Review source',
    path: '/roster/timesheets',
  },
  TIMESHEET: {
    reason: 'This timesheet is rejected or still needs approval and will not be paid yet.',
    owner: 'Payroll officer',
    label: 'Open timesheet',
    path: '/roster/timesheets',
  },
  REAPPROVAL: {
    reason: 'This timesheet needs individual review before re-approval.',
    owner: 'Payroll officer',
    label: 'Open timesheet',
    path: '/roster/timesheets',
  },
  SELF_REVIEW: {
    reason: 'A different payroll officer must review your own timesheet.',
    owner: 'Another payroll officer',
    label: 'Open timesheet',
    path: '/roster/timesheets',
  },
  OFFICER_SETUP: {
    reason:
      'An administrator must assign an eligible organisation administrator or payroll officer with exactly one active linked staff record.',
    owner: 'Administrator',
    label: 'Review payroll settings',
    path: '/settings/payroll',
  },
  REAPPROVAL_OFF: {
    reason:
      'Bulk re-approval is off. Review timesheets individually, or ask an administrator to review the policy.',
    owner: 'Payroll officer or administrator',
    label: 'Open timesheets',
    path: '/roster/timesheets',
  },
  HELD_SOURCE: {
    reason:
      'This pay input could not be priced. Fix the source, then choose Build a new version before approval, or acknowledge it as unpaid.',
    owner: 'Payroll officer',
    label: 'Review source',
    path: '/roster/timesheets',
  },
  FROZEN_SOURCE: {
    reason:
      'The source changed after capture. Before approval, replace the build; after approval, use the correction process.',
    owner: 'Payroll officer',
    label: 'Review pay run',
    path: '/payroll',
  },
  XERO_DRAFT: {
    reason: 'Choose one eligible Xero draft pay run, then refresh the preparation.',
    owner: 'Payroll officer',
    label: 'Review Xero preparation',
    path: '/payroll',
  },
  XERO_MAPPING: {
    reason:
      'The worker or earnings mapping needs review. Fix the mapping, then refresh the preparation.',
    owner: 'Payroll officer',
    label: 'Review Xero mappings',
    path: '/settings/integrations/xero',
  },
  XERO_SUPER: {
    reason:
      'The Xero pay template needs a valid superannuation line. Fix it in Xero, then refresh.',
    owner: 'Payroll officer',
    label: 'Open Xero settings',
    path: '/settings/integrations/xero',
  },
  XERO_DEDUCTIONS: {
    reason:
      'Review deductions and manually entered earnings in Xero, then refresh and review the plan.',
    owner: 'Payroll officer',
    label: 'Review Xero preparation',
    path: '/payroll',
  },
  XERO_LOADING: {
    reason: 'Confirm native leave loading in Xero, then refresh and review the preparation.',
    owner: 'Payroll officer',
    label: 'Open Xero settings',
    path: '/settings/integrations/xero',
  },
  XERO_BUDGET: {
    reason:
      'The daily Xero capacity is used. Review when capacity returns and schedule the remaining work for the next day.',
    owner: 'Payroll officer',
    label: 'Review schedule',
    path: '/payroll',
  },
  XERO_CONNECTION: {
    reason: 'The Xero connection is unavailable. Reconnect and retry the read before continuing.',
    owner: 'Payroll officer or administrator',
    label: 'Reconnect Xero',
    path: '/settings/integrations/xero',
  },
  UPDATED_PLAN: {
    reason: 'Xero or the plan changed. Review and approve the updated Xero plan before sending.',
    owner: 'Payroll officer',
    label: 'Review updated Xero plan',
    path: '/payroll',
  },
  PARTIAL_SEND: {
    reason:
      'Some results are uncertain or only partly sent. Check Xero and use controlled recovery; never send an attempted worker again.',
    owner: 'Payroll officer and Statura support',
    label: 'Check Xero and recovery',
    path: '/payroll',
  },
  PAYMENT_EVIDENCE: {
    reason: 'Record the bank payment reference before closing the pay run.',
    owner: 'Payroll officer',
    label: 'Add payment evidence',
    path: '/payroll',
  },
  SYSTEM: {
    reason: 'Something went wrong on our side. Your pay data is safe.',
    owner: 'Statura support',
    label: 'Contact Statura support',
    path: '/help',
  },
};

export function payrollBlockerKind(code: string | null | undefined): PayrollBlockerKind {
  const handoff = timesheetApprovalHandoff(code);
  if (handoff) return handoff.kind;
  const key = (code ?? '').toUpperCase();
  if (/SELF_APPROVAL/.test(key)) return 'SELF_REVIEW';
  if (
    /ASSIGNMENT_ACTOR_REQUIRED|OFFICER_REQUIRED|ASSIGNED_TO_ANOTHER_USER|IDENTITY_UNAVAILABLE/.test(
      key,
    )
  )
    return 'OFFICER_SETUP';
  if (/REAPPROVAL.*(OFF|DISABLED)/.test(key)) return 'REAPPROVAL_OFF';
  if (/PAY_IDENTITY|PAY_ROLE|CLASSIFICATION_CONFIGURATION/.test(key)) return 'PAY_ROLE';
  if (/NOT_REQUIRED|STOPPED/.test(key)) return 'STOPPED';
  if (
    /APPROVAL_|RECEIPT_UNAVAILABLE|EVIDENCE_INVALID|CAPTURED_VALUES_DIFFER|NOT_UNBOUND_APPROVED|GOVERNING_CORRECTION|FACT_REVIEW_REQUIRED|SOURCE_UNAVAILABLE|REVIEW_CHANGED/.test(
      key,
    )
  )
    return 'REAPPROVAL';
  if (/REJECTED|UNAPPROVED/.test(key)) return 'TIMESHEET';
  if (/^PRICING_|^PAYROLL_SALARY_HOLD_|^PAYROLL_TIMESHEET_HOLD_/.test(key)) return 'HELD_SOURCE';
  if (/FROZEN|SOURCE_DRIFT|PAYROLL_BOUND_OR_LOCKED/.test(key)) return 'FROZEN_SOURCE';
  if (/BUDGET|QUOTA|RATE_LIMIT|DAILY_CAPACITY/.test(key)) return 'XERO_BUDGET';
  if (/OAUTH|CONNECTIVITY|CONNECTION|AUTH_REQUIRED/.test(key)) return 'XERO_CONNECTION';
  if (/PARTIAL|UNKNOWN|AMBIGUOUS_POST|IN_FLIGHT|MISMATCH|ATTEMPTS_EXHAUSTED/.test(key))
    return 'PARTIAL_SEND';
  if (/BASELINE|PLAN.*(CHANGED|STALE)|UPDATED_PLAN/.test(key)) return 'UPDATED_PLAN';
  if (/SUPER/.test(key)) return 'XERO_SUPER';
  if (/DEDUCTION|NEGATIVE_NET|HUMAN.*EARNING/.test(key)) return 'XERO_DEDUCTIONS';
  if (/LOADING/.test(key)) return 'XERO_LOADING';
  if (/MAPPING|EMPLOYMENT_BASIS/.test(key)) return 'XERO_MAPPING';
  if (/DRAFT/.test(key)) return 'XERO_DRAFT';
  if (/PAYMENT_EVIDENCE/.test(key)) return 'PAYMENT_EVIDENCE';
  if (
    /PRICING_|RATE_CONFIGURATION|BREAK_FACTS|PAY_EVIDENCE|SOURCE_REMAINS_BLOCKED|PAY_CONTEXT_BLOCKED/.test(
      key,
    )
  )
    return 'HELD_SOURCE';
  return 'SYSTEM';
}

export function payrollBlockerRow(
  kind: PayrollBlockerKind,
  context: PayrollBlockerContext,
): PayrollBlockerRow {
  const entry = PAYROLL_BLOCKER_MATRIX[kind];
  const runHref = context.payRunId
    ? `/payroll/pay-runs/${encodeURIComponent(context.payRunId)}`
    : context.returnHref;
  let href = entry.path === '/payroll' ? runHref : entry.path;
  let reason = entry.reason;
  let label = entry.label;
  if (kind === 'REAPPROVAL' && context.reasonCode)
    reason = periodReapprovalReason(context.reasonCode);
  if (kind === 'HELD_SOURCE') {
    href =
      context.sourceType === 'LEAVE_SEGMENT'
        ? context.sourceRequestId
          ? `/roster/leave?${new URLSearchParams({ request_id: context.sourceRequestId })}`
          : '/roster/leave'
        : '/roster/timesheets';
    if (['TIMESHEET', 'TIMESHEET_SEGMENT'].includes(context.sourceType ?? '') && context.sourceId)
      href = `/roster/timesheets?${new URLSearchParams({ timesheet_id: context.sourceId, resolve: '1' })}`;
    if (context.sourceType === 'SALARY_PERIOD' && context.workerId)
      href = `/people/workers/${encodeURIComponent(context.workerId)}/pay-profile`;
    const namedReason =
      context.reasonCode === 'pricing_frozen_break_blocked'
        ? 'Break evidence needs review. This item stays unpaid. Open the timesheet to review the break.'
        : context.reasonCode
          ? (SALARY_HOLD_REASONS[context.reasonCode] ??
            TIMESHEET_HOLD_REASONS[context.reasonCode] ??
            ENGINE_HOLD_REASONS[context.reasonCode])
          : undefined;
    reason = `${namedReason ?? context.reason ?? 'This pay input needs review.'} ${context.approved ? 'Use the correction process for approved pay.' : 'Fix the source, then choose Build a new version before approval, or acknowledge it as unpaid.'}`;
  }
  if (kind === 'PAY_ROLE' && context.workerId) {
    const params = new URLSearchParams({
      effective_from: context.date,
      return_to: context.returnHref,
    });
    href = `/people/workers/${encodeURIComponent(context.workerId)}/pay-profile?${params}#assign-pay-profile`;
  }
  if (kind === 'PAY_ROLE' && !context.workerId && context.sourceId) {
    href = `/roster/timesheets?${new URLSearchParams({ timesheet_id: context.sourceId, resolve: '1' })}`;
    label = 'Open timesheet';
  }
  if (['TIMESHEET', 'REAPPROVAL', 'SELF_REVIEW', 'STOPPED'].includes(kind) && context.sourceId)
    href = `/roster/timesheets?${new URLSearchParams({ timesheet_id: context.sourceId, resolve: '1' })}`;
  if (kind === 'XERO_BUDGET' && context.capacityReturnsAt)
    reason += ` Capacity returns ${context.capacityReturnsAt}.`;
  if (kind === 'SYSTEM')
    href = `/help/tickets/new?${new URLSearchParams({ tier: 'STATURA', category: 'PLATFORM_ISSUE', subject: `Payroll calculation support reference ${context.id}`, path: context.returnHref })}`;
  const handoff = timesheetApprovalHandoff(context.reasonCode);
  if (handoff) {
    reason = handoff.reason;
    label = 'Open timesheet';
    href = context.sourceId
      ? `/roster/timesheets?${new URLSearchParams({ timesheet_id: context.sourceId, resolve: '1' })}`
      : '/roster/timesheets';
  }
  return {
    id: context.id,
    person: context.person,
    date: context.date,
    reason,
    owner: handoff?.owner ?? entry.owner,
    fix: { label, href },
  };
}

/** Re-approval exclusions retain the exact source reason, including locked/own-pay cases. */
export function payrollReapprovalBlockerRow(context: PayrollBlockerContext): PayrollBlockerRow {
  const row = payrollBlockerRow('REAPPROVAL', context);
  if (context.reasonCode === 'SELF_APPROVAL') return { ...row, owner: 'Another payroll officer' };
  if (
    context.reasonCode === 'IDENTITY_UNAVAILABLE' ||
    context.reasonCode === 'APPROVAL_ASSIGNMENT_ACTOR_REQUIRED'
  )
    return { ...row, owner: 'Administrator' };
  if (context.reasonCode === 'APPROVAL_ASSIGNED_TO_ANOTHER_USER')
    return { ...row, owner: 'Assigned payroll officer or administrator' };
  return row;
}
